import React, {useEffect, useMemo} from 'react'
import {observer} from 'mobx-react-lite'
import {Box, Button, Stack, useDisclosure} from '../../../../../vanilla'
import {useCustomerStore} from '../../../../../store/hooks/useStore'
import {SubmitHandler, useForm} from 'react-hook-form'
import {Modal} from '../../../../../components/modal'
import {AddressFields} from '../../../../../components/forms'
import {changeModalsButtonResponsive} from '../../../../../components/change-email-modal/styles.css'
import { AddressFormFields } from '../../../../../types/forms'

interface AddOrEditCustomerAddressModalProps extends ReturnType<typeof useDisclosure> {
  addressId: string | null
  onSubmitAddressModalForm: () => void
  onDismissAddressModalForm: () => void
}

export const AddOrEditCustomerAddressModal = observer(
  (props: AddOrEditCustomerAddressModalProps) => {
    const {addressId, onSubmitAddressModalForm, onDismissAddressModalForm, ...rest} = props

    const {addresses, updateCustomerAddress, createCustomerAddress, loading} = useCustomerStore()

    const address = useMemo(
      () => addresses.find((address) => address.addressId === addressId),
      [addresses, addressId]
    )

    const addressForm = useForm<AddressFormFields>({
      mode: 'onBlur',
    })

    const onSubmit: SubmitHandler<AddressFormFields> = async (data) => {
      const {searchForAddress, ...addressFormData} = data

      if (!addressFormData.addressId && addressFormData.address1) {
        addressFormData.addressId = addressFormData.address1
      }

      try {
        if (address) {
          await updateCustomerAddress({...addressFormData, c_updateSource: 'My Account'}, address.addressId)
        } else {
          await createCustomerAddress({...addressFormData, c_updateSource: 'My Account'})
        }

        if (onSubmitAddressModalForm && typeof onSubmitAddressModalForm === 'function') {
          onSubmitAddressModalForm()
        }
      } catch (e) {
        console.error('Error during creation/updating address', data.addressId)
      }
    }

    const clearFormOnClose = () => {
      try {
        addressForm.reset()
        onDismissAddressModalForm()
      } catch (error) {
        console.error('Error dismissing address modal', error)
      }
    }

    useEffect(() => {
      addressForm.reset({
        title: address?.title?.toLowerCase() || '',
        firstName: address?.firstName,
        lastName: address?.lastName || '',
        phone: address?.phone || '',
        addressId: address?.addressId || '',
        address1: address?.address1 || '',
        address2: address?.address2 || '',
        postalCode: address?.postalCode || '',
        city: address?.city || '',
        stateCode: address?.stateCode || '',
        preferred: address?.preferred || false,
        searchForAddress: address?.postalCode || '',
        countryCode: address?.countryCode || '',
      })
    }, [address])

    return (
      <Modal {...rest} onClose={clearFormOnClose} size="changeProfile" title="Edit delivery address">
        <Stack style={{maxHeight: '90dvh', overflowY: 'auto'}}>
          <form onSubmit={addressForm.handleSubmit(onSubmit)}>
           <AddressFields form={addressForm} inputGroupProps={{width: ['full', 'half']}} />
            <Box borderTop="1px" borderColor="gray200">
              <Button
                extraClassName={changeModalsButtonResponsive}
                marginTop="20px"
                marginBottom="24px"
                type="submit"
                size="sm"
                data-test-selector="save-changes"
                disabled={loading}
              >
                Save Changes
              </Button>
            </Box>
          </form>
        </Stack>
      </Modal>
    )
  }
)
